import type { FontVariant } from '@/services/fontsService';
import { toSecureImageUrl } from './imageUrl';

const loadedFonts = new Map<string, Promise<void>>();

// TEMPORARY: the CDN in front of the API (Imperva) serves its cached copies of the font files without the
// Access-Control-Allow-Origin header the backend sends, so the browser blocks them. A url the CDN hasn't
// seen goes to the backend and keeps the header. Each browser asks for its own url, the same for the whole
// day, so after the first download the browser's own cache (the backend allows a day) serves the file with
// the header. Remove once the CDN caches that header.
const FONT_CACHE_KEY_STORAGE = 'font-cache-key';
let browserFontKey: string | null = null;
let retryCount = 0;

const getBrowserFontKey = (): string => {
  if (browserFontKey) return browserFontKey;
  try {
    browserFontKey = localStorage.getItem(FONT_CACHE_KEY_STORAGE);
  } catch {
    // Storage blocked: a key for this page only.
  }
  if (!browserFontKey) {
    browserFontKey = Math.random().toString(36).slice(2, 10);
    try {
      localStorage.setItem(FONT_CACHE_KEY_STORAGE, browserFontKey);
    } catch {
      // Same as above.
    }
  }
  return browserFontKey;
};

const withQuery = (url: string, value: string) => `${url}${url.includes('?') ? '&' : '?'}fv=${value}`;

// Changes daily so the browser never revalidates against the CDN's copy.
const browserCdnUrl = (url: string) =>
  withQuery(url, `${getBrowserFontKey()}${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`);

// For when the browser dropped its copy and the CDN already cached this browser's url.
const uniqueCdnUrl = (url: string) => {
  retryCount += 1;
  return withQuery(url, `${Date.now().toString(36)}${retryCount}`);
};

const loadFontFace = async (family: string, url: string) => {
  const fontFace = await new FontFace(family, `url("${url}")`).load();
  document.fonts.add(fontFace);
};

// Registers the variant's TTF under its id (the `font_family` used by the canvas and the backend).
// Calls are cached per variant; a failed load is evicted so it can be retried.
export const loadFontVariant = (variant: FontVariant): Promise<void> => {
  const cached = loadedFonts.get(variant.id);
  if (cached) return cached;

  // The backend lists http:// urls that redirect to https:// (blocked as mixed content on the https site,
  // and one more hop): load the https one directly. Fonts always need CORS, so the file's response must
  // also carry Access-Control-Allow-Origin.
  const url = toSecureImageUrl(variant.url);
  const loading = loadFontFace(variant.id, browserCdnUrl(url))
    .catch(() => loadFontFace(variant.id, uniqueCdnUrl(url)))
    .catch((error: unknown) => {
      loadedFonts.delete(variant.id);
      throw error;
    });

  loadedFonts.set(variant.id, loading);
  return loading;
};

// "NTTaDa-Bold" -> { family: "NTTaDa", variant: "Bold" }; ids without "-" have no variant.
export const parseFontId = (fontId: string): { family: string; variant: string | null } => {
  const separatorIndex = fontId.indexOf('-');
  if (separatorIndex === -1) return { family: fontId, variant: null };
  return { family: fontId.slice(0, separatorIndex), variant: fontId.slice(separatorIndex + 1) };
};

// "BarlowSemiCondensed" -> "Barlow Semi Condensed". Display only; never use it as a font family.
export const formatFontName = (name: string): string =>
  name
    .replace(/_/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

export const resetFontLoaderCache = () => {
  loadedFonts.clear();
  browserFontKey = null;
};
